import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { database } from "../../database/firebase";
import "./leaderboard.css";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function normalizeCharacterName(character) {
  const value = String(character || "").trim().toLowerCase();
  return /^c[1-6]$/.test(value) ? value : "";
}

function getDisplayName(userRow) {
  const name = String(userRow?.name || "").trim();
  if (name && name.toLowerCase() !== "null null") return name;
  const email = String(userRow?.email || "").trim();
  if (email) return email.split("@")[0] || "Player";
  return "Player";
}

function formatRole(role) {
  const value = String(role || "").trim().toLowerCase();
  if (!value) return "-";
  if (value === "imposter") return "Imposter";
  if (value === "jester") return "Jester";
  if (value === "player" || value === "normal") return "Player";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function Leaderboard() {
  const location = useLocation();
  const userData = useMemo(() => readStoredUserData(), []);
  const sessionId = String(
    userData?.sessionId || userData?.sessionID || userData?.session_id || ""
  ).trim();
  const lobbyName = String(
    userData?.lobby ||
      userData?.lobbyName ||
      userData?.lobby_name ||
      location.state?.lobbyName ||
      location.state?.lobby ||
      ""
  ).trim();

  const [usersMap, setUsersMap] = useState({});
  const [playerStatsMap, setPlayerStatsMap] = useState({});
  const [winnerSummary, setWinnerSummary] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    if (!sessionId || !lobbyName) {
      setLoadingUsers(false);
      return undefined;
    }

    const usersRef = collection(database, `lobbies/${sessionId}/lobby/${lobbyName}/users`);
    const unsub = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const uid = String(row?.userId || d.id || "").trim();
          if (!uid) return;
          next[uid] = row;
        });
        setUsersMap(next);
        setLoadingUsers(false);
      },
      () => setLoadingUsers(false)
    );

    return () => unsub();
  }, [lobbyName, sessionId]);

  useEffect(() => {
    if (!sessionId || !lobbyName) return undefined;

    const liveRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/state/gameover`);
    const unsub = onSnapshot(liveRef, (snap) => {
      const data = snap.data() || {};
      const stats =
        data?.playerStats && typeof data.playerStats === "object"
          ? data.playerStats
          : {};
      setPlayerStatsMap(stats);
    });

    return () => unsub();
  }, [lobbyName, sessionId]);

  useEffect(() => {
    if (!sessionId || !lobbyName) return undefined;
    const puzzleRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/state/puzzle`);
    const lobbyRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
    let latestLobbySummary = null;
    let latestPuzzleSummary = null;
    const applySummary = () => {
      setWinnerSummary(latestLobbySummary || latestPuzzleSummary || null);
    };
    const unsubPuzzle = onSnapshot(puzzleRef, (snap) => {
      const data = snap.data() || {};
      const voting = data?.voting && typeof data.voting === "object" ? data.voting : {};
      const finalOutcome =
        voting?.finalOutcome && typeof voting.finalOutcome === "object" ? voting.finalOutcome : null;
      latestPuzzleSummary = finalOutcome;
      applySummary();
    });
    const unsubLobby = onSnapshot(lobbyRef, (snap) => {
      const data = snap.data() || {};
      const summary =
        data?.winnerSummary && typeof data.winnerSummary === "object" ? data.winnerSummary : null;
      latestLobbySummary = summary;
      applySummary();
    });
    return () => {
      unsubPuzzle();
      unsubLobby();
    };
  }, [lobbyName, sessionId]);

  const { ranked, winnerIdSet } = useMemo(() => {
    const knownIds = new Set([
      ...Object.keys(usersMap || {}),
      ...Object.keys(playerStatsMap || {}),
    ]);
    const rows = Array.from(knownIds).map((uid) => {
      const u = usersMap?.[uid] || {};
      const s = playerStatsMap?.[uid] || {};
      const score = Number.isFinite(Number(s?.score))
        ? Number(s.score)
        : Number(u?.score || 0);
      const wrongAnswers = Number.isFinite(Number(s?.wrongAnswers))
        ? Number(s.wrongAnswers)
        : Number(u?.wrongAnswers || 0);
      return {
        userId: uid,
        score,
        wrongAnswers,
        name: getDisplayName({ ...u, ...s }),
        character: normalizeCharacterName(s?.character) || normalizeCharacterName(u?.character),
        role: String(u?.role || s?.role || "").trim().toLowerCase(),
        status: String(u?.status || "live").trim().toLowerCase(),
      };
    });
    const group = String(winnerSummary?.winnerRoleGroup || "").trim().toLowerCase();
    const reason = String(winnerSummary?.reason || "").trim().toLowerCase();
    const isPlayersWin = group === "others" || reason === "others_puzzle_completed";

    const winnerIds = new Set(
      (winnerSummary?.winnerUserIds || []).map((id) => String(id || "").trim()).filter(Boolean)
    );
    if (isPlayersWin) {
      // Include all player-role users even if voted out — the team wins
      // together regardless of who was eliminated mid-round.
      rows.forEach((r) => {
        const role = String(r.role || "").trim().toLowerCase();
        if (role !== "player" && role !== "normal") return;
        winnerIds.add(r.userId);
      });
    }

    if (winnerIds.size > 0) {
      const sorted = rows
        .sort((a, b) => {
          const aWinner = winnerIds.has(a.userId) ? 1 : 0;
          const bWinner = winnerIds.has(b.userId) ? 1 : 0;
          if (aWinner !== bWinner) return bWinner - aWinner;
          if (b.score !== a.score) return b.score - a.score;
          return a.name.localeCompare(b.name);
        })
        .slice(0, 8);
      return { ranked: sorted, winnerIdSet: winnerIds };
    }
    const sorted = rows
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if (a.wrongAnswers !== b.wrongAnswers) return a.wrongAnswers - b.wrongAnswers;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 4);
    return { ranked: sorted, winnerIdSet: new Set() };
  }, [playerStatsMap, usersMap, winnerSummary]);

  const winners = useMemo(
    () =>
      winnerIdSet.size > 0
        ? ranked.filter((p) => winnerIdSet.has(p.userId))
        : ranked.slice(0, 1),
    [ranked, winnerIdSet]
  );
  const losers = useMemo(() => {
    if (winnerIdSet.size === 0) return ranked.slice(1);
    const list = ranked.filter((p) => !winnerIdSet.has(p.userId));
    const order = (role) => {
      const r = String(role || "").trim().toLowerCase();
      if (r === "imposter") return 0;
      if (r === "jester") return 1;
      return 2;
    };
    return [...list].sort((a, b) => order(a.role) - order(b.role) || a.name.localeCompare(b.name));
  }, [ranked, winnerIdSet]);

  const headlineLabel = useMemo(() => {
    const group = String(winnerSummary?.winnerRoleGroup || "").trim().toLowerCase();
    if (group === "imposter") return "IMPOSTER WINNER";
    if (group === "jester") return "JESTER WINNER";
    if (group === "others") return "PLAYERS WINNERS";
    return "GAME OVER";
  }, [winnerSummary?.winnerRoleGroup]);

  return (
    <div className="bnb-leaderboard-page">

      {!sessionId || !lobbyName ? (
        <div className="bnb-empty-state">Missing lobby/session details.</div>
      ) : loadingUsers && ranked.length === 0 ? (
        <div className="bnb-empty-state">Loading leaderboard...</div>
      ) : ranked.length === 0 ? (
        <div className="bnb-empty-state">No leaderboard data yet.</div>
      ) : (
        <div className="bnb-podium">
          <div className="bnb-winner-title">{headlineLabel}</div>

          <div className="bnb-winners-row">
            {winners.map((player, idx) => (
              <div
                key={player.userId || `winner-${player.name}-${idx}`}
                className={`bnb-winner-card bnb-winner-anim${
                  String(player?.role || "").trim().toLowerCase() === "imposter"
                    ? " bnb-imposter-card"
                    : ""
                }`}
              >
                <div className="bnb-avatar-frame bnb-winner-avatar">
                  {player?.character ? (
                    <img src={`/img/${player.character}.png`} alt={player.name} />
                  ) : (
                    <span>🙂</span>
                  )}
                </div>
                <div className="bnb-player-name">{player?.name || "Player"}</div>
                <div className="bnb-player-role">{formatRole(player?.role)}</div>
              </div>
            ))}
          </div>

          {losers.length > 0 ? (
            <div className="bnb-losers-row">
              {losers.map((player, idx) => (
                <div key={player.userId || `loser-${player.name}-${idx}`} className="bnb-runner-col">
                  <div
                    className={`bnb-avatar-frame bnb-runner-avatar bnb-runner-${idx + 2}${
                      String(player?.role || "").trim().toLowerCase() === "imposter"
                        ? " bnb-imposter-card"
                        : ""
                    }`}
                  >
                    {player?.character ? (
                      <img src={`/img/${player.character}.png`} alt={player.name} />
                    ) : (
                      <span>🙂</span>
                    )}
                  </div>
                  <div className="bnb-runner-name">{player?.name || "Player"}</div>
                  <div className="bnb-player-role">{formatRole(player?.role)}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}